import React, {
  type AnchorHTMLAttributes,
  type ForwardRefExoticComponent,
  type ReactNode,
} from "react";
import type { BaseProps } from "@purpur/common-types";
import { Heading, type HeadingTagType, type HeadingVariantType } from "@purpur/heading";
import c from "classnames/bind";

import styles from "./card-heading.module.scss";
import { CardTriggerItem } from "./card-trigger-item";

const cx = c.bind(styles);

export type CardHeadingDefaultProps = {
  /**
   * Use this to set a testid on the feature list
   */
  ["data-testid"]?: string;
  title: string;
  /**
   * Use when you want an icon to the left of the title
   */
  icon?: ReactNode;
  /**
   * Use for the CTA, it will positioned to the right
   */
  children?: ReactNode;
  headingTag?: HeadingTagType;
  variant?: HeadingVariantType;
  enableHyphenation?: boolean;
};

export type CardHeadingNonInteractiveProps = {
  disabled?: never;
  href?: never;
  /**
   * If the href prop is not provided the clickable element will be a button and use this clickHandler when the user presses the card.
   */
  onClick?: never;
  linkElement?: never;
};

export type CardHeadingLinkProps = {
  /**
   * Use this to disable the card. Can be used both with a link card and a button card. In the case of the button the clickHandler will not be called and if a anchor tag the href will be removed.
   */
  disabled?: boolean;
  /**
   * Makes the card become a link to the provided href.
   */
  href: string;
  /**
   * If the href prop is not provided the clickable element will be a button and use this clickHandler when the user presses the card.
   */
  onClick?: () => void;
  linkElement?: ForwardRefExoticComponent<AnchorHTMLAttributes<HTMLAnchorElement>> | "a";
};

export type CardHeadingButtonProps = {
  /**
   * Use this to disable the card. Can be used both with a link card and a button card. In the case of the button the clickHandler will not be called and if a anchor tag the href will be removed.
   */
  disabled?: boolean;
  /**
   * Makes the card become a link to the provided href.
   */
  href?: never;
  /**
   * If the href prop is not provided the clickable element will be a button and use this clickHandler when the user presses the card.
   */
  onClick: () => void;
  linkElement?: never;
};

export type CardHeadingProps<T extends HeadingTagType = "h2"> = BaseProps<T> &
  CardHeadingDefaultProps;

const rootClassName = "purpur-card-heading";

export const CardHeading = <T extends HeadingTagType = "h2">({
  ["data-testid"]: dataTestId = "purpur-card-heading",
  ["aria-label"]: ariaLabel,
  title,
  icon,
  children,
  headingTag = "h2" as T,
  enableHyphenation,
  onClick = undefined,
  href = undefined,
  disabled = false,
  linkElement = "a",
  variant = "title-100",
  ...props
}: CardHeadingProps &
  (CardHeadingLinkProps | CardHeadingButtonProps | CardHeadingNonInteractiveProps)) => {
  const classes = cx(rootClassName, {
    [`${rootClassName}--is-interactive`]: onClick || href,
    [`${rootClassName}--disabled`]: disabled,
  });

  return (
    <div className={classes} data-testid={dataTestId}>
      <div className={cx(`${rootClassName}__header`)}>
        {icon && <div className={cx(`${rootClassName}__icon`)}>{icon}</div>}
        <Heading
          tag={headingTag}
          className={cx(`${rootClassName}__heading`)}
          variant={variant}
          enableHyphenation={enableHyphenation}
          {...props}
        >
          {onClick || href ? (
            <CardTriggerItem
              aria-label={ariaLabel}
              disabled={disabled}
              href={href}
              onClick={onClick}
              linkElement={linkElement}
            >
              {title}
            </CardTriggerItem>
          ) : (
            title
          )}
        </Heading>
      </div>
      {children}
    </div>
  );
};
